import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import {
  MdLocalFlorist,
  MdPrint,
  MdTimeline,
  MdFlag,
  MdRefresh,
  MdTableView,
} from "react-icons/md";
import { exportToExcel } from "react-json-to-excel";

import axios from "../axiosConfig";
import { BASE_SERVER_URL } from "../sampleData";
import { replaceCharacter, toName } from "../components";
import CalendarMonth, {
  startOfDay,
  shiftDays,
  shiftMonth,
  monthOf,
} from "../components/CalendarMonth";
import "../ordered-products.css";

/* ------------------------------------------------------------------ *
 * Ordered products (redesign) — /ordered-products
 *
 * Replaces src/pages/OrderedProducts.js (kept at /ordered-products-v1):
 *   - bootstrap striped table + inline widget column
 *                                     -> two sectioned cards (Funeral,
 *                                        Retail) with a sticky dark
 *                                        header band, one row per
 *                                        ordered product
 *   - three mode toggles fighting over
 *     the same colSpan                -> "Assign" and "Progress" toggles
 *                                        that add their own columns
 *   - a floating flower slide-over    -> a proper drawer with a scrim
 *   - a staff list that was never
 *     fetched (setUsers was dead)     -> GET /users/getAll
 *   - (new)                           -> KPI strip, type/assignment
 *                                        filters, product search
 *
 * Every legacy column is kept 1:1: #, Type, Product Name (+ comment),
 * Size, Amount, Price, Content, Assigned To, and in progress mode Start
 * Time / Check 1 / Check 2 / Check 3 / End Time.
 *
 * Same endpoints as the legacy page:
 *   POST /ordered-products/by-date-range  { dateRange, mergeOrders }
 *   GET  /api/flowers/get-all
 *   POST /wo/assignOrderedProducts        { assigning, user, ordered_product_ids }
 *   GET  /users/getAll                    (staff list for allocation)
 * and the same print URL, /ordered-products/print/DD-MM-YYYY.
 * ------------------------------------------------------------------ */

const DATE_FMT = "DD/MM/YYYY";

const PRESETS = [
  { label: "Today", from: 0, to: 0 },
  { label: "Tomorrow", from: 1, to: 1 },
  { label: "Next 7 days", from: 0, to: 6 },
  { label: "This week", week: true },
];

const initials = (name) =>
  String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";

const fmtStamp = (raw) => {
  const n = Number(raw);
  if (!n) return null;
  const m = moment(n);
  return { day: m.format("D MMM"), time: m.format("h:mm a") };
};

const money = (p) => {
  const n = Number(p);
  return isFinite(n) ? `$${n.toFixed(2)}` : `$${p || 0}`;
};

/* The flower rows a product carries at its ordered size. `flower_content`
   is keyed by size and each entry points at a flower document by id — the
   names come from /api/flowers/get-all, the same join the legacy page did. */
function contentOf(product) {
  const detail = product.product_detail || {};
  const content = detail.flower_content || {};
  return content[product.size] || [];
}

const progressOf = (product) => (product.orderProgress || [])[0] || {};
const assigneeOf = (product) => (progressOf(product).assigned_to || {}).full_name || "";

export function OrderedProductsRedesign() {
  /* ---------------- date range ---------------- */
  const today = useMemo(() => startOfDay(Date.now()), []);
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [preset, setPreset] = useState("Today");

  const [calOpen, setCalOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState(today);
  const [draftTo, setDraftTo] = useState(today);
  const [viewMonth, setViewMonth] = useState(() => monthOf(today));
  const calRef = useRef(null);

  /* ---------------- data ---------------- */
  const [orders, setOrders] = useState({ Funeral: {}, Retail: [] });
  const [flowerCount, setFlowerCount] = useState({});
  const [flowerList, setFlowerList] = useState({});
  const [typeTally, setTypeTally] = useState({});
  const [orderTally, setOrderTally] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  /* ---------------- modes + filters ---------------- */
  const [assignMode, setAssignMode] = useState(false);
  const [progressMode, setProgressMode] = useState(false);
  const [showFlowers, setShowFlowers] = useState(false);
  const [typeFilter, setTypeFilter] = useState("All");
  const [stateFilter, setStateFilter] = useState("Any");
  const [query, setQuery] = useState("");

  /* ---------------- allocation ---------------- */
  const [picked, setPicked] = useState([]);
  const [staff, setStaff] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [saving, setSaving] = useState(false);

  const range = useMemo(
    () => ({
      startDate: moment(from).format(DATE_FMT),
      endDate: moment(to).format(DATE_FMT),
    }),
    [from, to]
  );

  /* ---------------- fetching ---------------- */
  const refreshList = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    axios
      .post(BASE_SERVER_URL + "/ordered-products/by-date-range", {
        dateRange: { startDate: range.startDate, endDate: range.endDate },
        mergeOrders: true,
      })
      .then((doc) => {
        if (doc.data.status !== "success") {
          setLoading(false);
          setLoadError(true);
          return;
        }
        const body = doc.data.body || {};
        setOrders(body.products || { Funeral: {}, Retail: [] });
        setFlowerCount(body.flowerCount || {});
        setOrderTally((body.OrderCountByType || {}).list || {});
        setTypeTally(body.TypeCount || {});
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        setLoadError(true);
      });
  }, [range.startDate, range.endDate]);

  useEffect(refreshList, [refreshList]);

  /* Flower names, fetched once — the content column joins against them. */
  useEffect(() => {
    axios
      .get(BASE_SERVER_URL + "/api/flowers/get-all")
      .then((doc) => {
        if (!doc.data.success) return;
        const byId = {};
        (doc.data.data || []).forEach((f) => {
          byId[f._id] = f;
        });
        setFlowerList(byId);
      })
      .catch(() => {});
  }, []);

  /* Staff, fetched the first time the allocate panel opens. The legacy
     page declared `users` but never filled it, so its list was empty. */
  useEffect(() => {
    if (!assignMode || staff.length || staffLoading) return;
    setStaffLoading(true);
    axios
      .get(BASE_SERVER_URL + "/users/getAll")
      .then((doc) => {
        setStaff((doc.data || {}).response || []);
        setStaffLoading(false);
      })
      .catch(() => setStaffLoading(false));
  }, [assignMode, staff.length, staffLoading]);

  /* Leaving assign mode drops the selection — it would only go stale. */
  useEffect(() => {
    if (!assignMode) {
      setPicked([]);
      setSelectedStaff(null);
    }
  }, [assignMode]);

  /* Calendar popover: close on outside click or Escape. */
  useEffect(() => {
    if (!calOpen) return undefined;
    const onDown = (e) => {
      if (calRef.current && !calRef.current.contains(e.target)) setCalOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setCalOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [calOpen]);

  /* ---------------- rows ---------------- */
  /* Funeral products arrive keyed by funeral id; retail as a flat list.
     Both end up as {key, name, products} groups so one renderer covers
     them — retail keeps the legacy's one-row-per-product shape by making
     each product its own group. `day_count === 0` retail rows were
     skipped by the legacy page and still are. */
  const funeralGroups = useMemo(() => {
    const src = orders.Funeral || {};
    return Object.keys(src).map((id) => {
      const products = src[id] || [];
      const lead = products[0] || {};
      return {
        key: id,
        name: (lead.parent_work_order || {}).late_person_name || "Unnamed funeral",
        products,
      };
    });
  }, [orders]);

  const retailProducts = useMemo(
    () => (orders.Retail || []).filter((p) => p.day_count !== 0),
    [orders]
  );

  const matches = useCallback(
    (p) => {
      if (stateFilter === "Assigned" && !assigneeOf(p)) return false;
      if (stateFilter === "Unassigned" && assigneeOf(p)) return false;
      if (!query.trim()) return true;
      const q = query.trim().toLowerCase();
      return [p.product_name, p.product_type, p.size, p.comment, assigneeOf(p)]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().indexOf(q) !== -1);
    },
    [stateFilter, query]
  );

  const visibleFuneral = useMemo(
    () =>
      funeralGroups
        .map((g) => ({ ...g, products: g.products.filter(matches) }))
        .filter((g) => g.products.length),
    [funeralGroups, matches]
  );
  const visibleRetail = useMemo(
    () => retailProducts.filter(matches),
    [retailProducts, matches]
  );

  /* ---------------- tallies ---------------- */
  const allProducts = useMemo(
    () => funeralGroups.flatMap((g) => g.products).concat(retailProducts),
    [funeralGroups, retailProducts]
  );
  const stats = useMemo(() => {
    let units = 0;
    let assigned = 0;
    let done = 0;
    allProducts.forEach((p) => {
      units += Number(p.amount) || 0;
      if (assigneeOf(p)) assigned += 1;
      if (progressOf(p).ended) done += 1;
    });
    return {
      lines: allProducts.length,
      units,
      assigned,
      unassigned: allProducts.length - assigned,
      done,
      funerals: funeralGroups.length,
    };
  }, [allProducts, funeralGroups]);

  /* ---------------- selection ---------------- */
  const pickedSet = useMemo(() => new Set(picked), [picked]);

  function togglePick(id) {
    setPicked((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : prev.concat(id)
    );
  }

  function toggleMany(ids) {
    const allOn = ids.length > 0 && ids.every((id) => pickedSet.has(id));
    setPicked((prev) =>
      allOn
        ? prev.filter((x) => !ids.includes(x))
        : prev.concat(ids.filter((id) => !prev.includes(id)))
    );
  }

  function groupState(ids) {
    const on = ids.filter((id) => pickedSet.has(id)).length;
    return on === 0 ? "" : on === ids.length ? " is-on" : " is-some";
  }

  /* ---------------- allocate / deallocate ---------------- */
  function assign(remove) {
    if (!picked.length) return;
    if (!remove && !selectedStaff) return;
    setSaving(true);
    axios
      .post(BASE_SERVER_URL + "/wo/assignOrderedProducts", {
        assigning: !remove,
        user: remove
          ? {}
          : {
              assigned_to: selectedStaff,
              start: String(Date.now()),
              ended: "",
              checked_1: "",
              checked_2: "",
              checked_3: "",
            },
        ordered_product_ids: picked,
      })
      .then((doc) => {
        setSaving(false);
        if (doc.data.status !== "success") {
          toast.error("Couldn't update the allocation, please try again.");
          return;
        }
        toast.success(
          remove
            ? `${picked.length} products unallocated.`
            : `${picked.length} products allocated to ${
                (selectedStaff || {}).full_name || "staff"
              }.`
        );
        setPicked([]);
        setSelectedStaff(null);
        refreshList();
      })
      .catch(() => {
        setSaving(false);
        toast.error("Couldn't reach the server.");
      });
  }

  /* ---------------- date range controls ---------------- */
  function applyPreset(p) {
    setPreset(p.label);
    if (p.week) {
      setFrom(startOfDay(moment().startOf("isoWeek").valueOf()));
      setTo(startOfDay(moment().endOf("isoWeek").valueOf()));
    } else {
      setFrom(shiftDays(today, p.from));
      setTo(shiftDays(today, p.to));
    }
    setCalOpen(false);
  }

  function openCal() {
    setDraftFrom(from);
    setDraftTo(to);
    setViewMonth(monthOf(to));
    setCalOpen(true);
  }

  function pickDay(stamp) {
    if (!draftFrom || (draftFrom && draftTo)) {
      setDraftFrom(stamp);
      setDraftTo(null);
    } else if (stamp < draftFrom) {
      setDraftTo(draftFrom);
      setDraftFrom(stamp);
    } else {
      setDraftTo(stamp);
    }
  }

  function applyCal() {
    if (!draftFrom || !draftTo) return;
    setFrom(draftFrom);
    setTo(draftTo);
    setPreset(null);
    setCalOpen(false);
  }

  const leftMonth = shiftMonth(viewMonth, -1);
  const sameDay = from === to;
  const rangeLabel = sameDay
    ? moment(from).format("ddd D MMM YYYY")
    : `${moment(from).format("D MMM")} – ${moment(to).format("D MMM YYYY")}`;
  const rangeDays = `${Math.round((to - from) / 86400000) + 1} day${
    from === to ? "" : "s"
  }`;

  /* The flower list as a spreadsheet, A–Z like the drawer — the same
     react-json-to-excel export the legacy FlowerContentList offered. */
  function exportFlowers() {
    const rows = Object.keys(flowerCount)
      .map((k) => [k, Number(flowerCount[k]) || 0])
      .sort(byName)
      .map(([k, v]) => ({ Flower: toName(k), Amount: v }));
    const range = sameDay
      ? moment(from).format("DD-MM-YYYY")
      : `${moment(from).format("DD-MM-YYYY")}_to_${moment(to).format("DD-MM-YYYY")}`;
    exportToExcel(rows, `flower_content_${range}`);
  }

  function printList() {
    window.open(
      "/ordered-products/print/" + replaceCharacter(range.startDate, "/", "-"),
      "_blank"
    );
  }

  /* ---------------- column plumbing ----------------
     The legacy page swapped Price + Content out for the five progress
     columns; keep exactly that, and keep the extra checkbox column at
     the end in assign mode. */
  const colCount = 6 + (progressMode ? 5 : 2) + (assignMode ? 1 : 0);

  const head = (
    <thead>
      <tr>
        <th className="op-cell-num">#</th>
        <th>Type</th>
        <th className="is-left">Product Name</th>
        <th>Size</th>
        <th>Amount</th>
        {!progressMode && <th>Price</th>}
        {!progressMode && <th className="is-left">Content</th>}
        <th>Assigned To</th>
        {progressMode && <th>Start Time</th>}
        {progressMode && <th>Check 1</th>}
        {progressMode && <th>Check 2</th>}
        {progressMode && <th>Check 3</th>}
        {progressMode && <th>End Time</th>}
        {assignMode && <th className="op-pick" />}
      </tr>
    </thead>
  );

  function contentCell(product) {
    const rows = contentOf(product);
    if (!rows.length) return <span className="op-nocontent">No flower content</span>;
    return rows.map((f, i) => {
      const name = (flowerList[f.flower_id] || {}).flower_name;
      return (
        <span key={i} className={`op-flower${name ? "" : " is-unknown"}`}>
          {name || "Unknown flower"}
          <b>×{f.amount || 0}</b>
        </span>
      );
    });
  }

  function assignedCell(product) {
    const who = assigneeOf(product);
    if (who) {
      return (
        <span className="op-assigned">
          <span className="op-avatar">{initials(who)}</span>
          <span className="op-assigned-name">{who}</span>
        </span>
      );
    }
    return (
      <span className="op-unassigned is-inert">
        <MdFlag />
        Not assigned
      </span>
    );
  }

  function stampCell(raw) {
    const s = fmtStamp(raw);
    if (!s) return <span className="op-check is-empty">—</span>;
    return (
      <span className="op-stamp">
        <b>{s.day}</b>
        {s.time}
      </span>
    );
  }

  function checkCell(value) {
    return value ? (
      <span className="op-check">{value}</span>
    ) : (
      <span className="op-check is-empty">—</span>
    );
  }

  function productRow(product, num) {
    const progress = progressOf(product);
    const isPicked = pickedSet.has(product._id);
    return (
      <tr
        key={product._id || num}
        className={`op-row${isPicked ? " is-picked" : ""}`}
        onClick={assignMode ? () => togglePick(product._id) : undefined}
      >
        <td className="op-cell-num">{num}</td>
        <td>
          <span className="op-type">{product.product_type}</span>
        </td>
        <td className="op-cell-name">
          <span className="op-name">{product.product_name}</span>
          {product.comment && (
            <span className="op-comment">Comment: {product.comment}</span>
          )}
        </td>
        <td>{toName(product.size)}</td>
        <td className="op-amount">{product.amount}</td>
        {!progressMode && (
          <td className="op-price">
            {money(product.customPrice ? product.customPrice : product.price)}
          </td>
        )}
        {!progressMode && <td className="op-cell-content">{contentCell(product)}</td>}
        <td>{assignedCell(product)}</td>
        {progressMode && <td>{stampCell(progress.start)}</td>}
        {progressMode && <td>{checkCell(progress.checked_1)}</td>}
        {progressMode && <td>{checkCell(progress.checked_2)}</td>}
        {progressMode && <td>{checkCell(progress.checked_3)}</td>}
        {progressMode && <td>{stampCell(progress.ended)}</td>}
        {assignMode && (
          <td className="op-pick">
            <span className={`op-box${isPicked ? " is-on" : ""}`}>
              {isPicked ? "✓" : ""}
            </span>
          </td>
        )}
      </tr>
    );
  }

  const showFuneral = typeFilter === "All" || typeFilter === "Funeral";
  const showRetail = typeFilter === "All" || typeFilter === "Retail";

  return (
    <div className="op-page">
      {/* ---------------- toolbar ---------------- */}
      <div className="op-bar">
        <div className="op-presets">
          {PRESETS.map((p) => (
            <button
              key={p.label}
              type="button"
              className={preset === p.label ? "is-active" : ""}
              onClick={() => applyPreset(p)}
            >
              {p.label}
            </button>
          ))}
        </div>

        <div className="op-cal-wrap" ref={calRef}>
          <button
            type="button"
            className={`op-range-btn${calOpen ? " is-open" : ""}`}
            aria-expanded={calOpen}
            onClick={() => (calOpen ? setCalOpen(false) : openCal())}
          >
            <span className="rf-num">{rangeLabel}</span>
            <span className="op-range-days rf-num">{rangeDays}</span>
            <span className="op-caret">▾</span>
          </button>

          {calOpen && (
            <div className="op-cal">
              <div className="op-cal-head">
                <span>Custom range</span>
                <button
                  type="button"
                  className="op-cal-close"
                  onClick={() => setCalOpen(false)}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className="op-cal-months">
                <CalendarMonth
                  year={leftMonth.year}
                  monthIndex={leftMonth.monthIndex}
                  from={draftFrom}
                  to={draftTo}
                  onPick={pickDay}
                  onPrev={() => setViewMonth((m) => shiftMonth(m, -1))}
                />
                <CalendarMonth
                  year={viewMonth.year}
                  monthIndex={viewMonth.monthIndex}
                  from={draftFrom}
                  to={draftTo}
                  onPick={pickDay}
                  onNext={() => setViewMonth((m) => shiftMonth(m, 1))}
                />
              </div>
              <div className="op-cal-foot">
                <span>
                  {draftFrom && draftTo
                    ? `${moment(draftFrom).format("D MMM")} – ${moment(draftTo).format(
                        "D MMM YYYY"
                      )}`
                    : "Pick the end of the range"}
                </span>
                <button
                  type="button"
                  className="rf-btn rf-btn-ghost"
                  onClick={() => setCalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="rf-btn rf-btn-primary"
                  onClick={applyCal}
                  disabled={!draftFrom || !draftTo}
                >
                  Apply
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="rf-segmented">
          {["All", "Funeral", "Retail"].map((t) => (
            <button
              key={t}
              type="button"
              className={typeFilter === t ? "is-active" : ""}
              onClick={() => setTypeFilter(t)}
            >
              {t}
            </button>
          ))}
        </div>

        <label className="op-select">
          <span className="op-select-k">Status</span>
          <select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}>
            <option>Any</option>
            <option>Assigned</option>
            <option>Unassigned</option>
          </select>
        </label>

        <label className="op-search">
          <span className="op-search-icon" />
          <input
            type="search"
            value={query}
            placeholder="Search product, size, staff"
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>

        <span className="rf-spacer" />

        <button
          type="button"
          className="rf-btn rf-btn-ghost"
          onClick={refreshList}
          title="Reload"
        >
          <MdRefresh style={{ verticalAlign: "-2px" }} /> Reload
        </button>
        <button
          type="button"
          className="rf-btn rf-btn-ghost"
          onClick={() => setShowFlowers(true)}
        >
          <MdLocalFlorist style={{ verticalAlign: "-2px" }} /> Content flowers
        </button>
        <button type="button" className="rf-btn rf-btn-ghost" onClick={printList}>
          <MdPrint style={{ verticalAlign: "-2px" }} /> Print list
        </button>
        <button
          type="button"
          className={`rf-btn ${progressMode ? "rf-btn-ghost op-btn-on" : "rf-btn-ghost"}`}
          onClick={() => {
            setProgressMode((v) => !v);
            setAssignMode(false);
          }}
        >
          <MdTimeline style={{ verticalAlign: "-2px" }} /> Progress
        </button>
        {/* Assign tasks — hidden on request. The allocation machinery below
            (pick column, staff panel, POST /wo/assignOrderedProducts) is kept
            intact; restore this button to bring the mode back. The only other
            way into assign mode, the "Not assigned" pill, is inert while the
            button is gone — without it there would be no way back out. */}
      </div>

      {/* ---------------- KPI strip ---------------- */}
      <div className="op-kpis">
        <div className="op-kpi">
          <span className="op-kpi-k">Product lines</span>
          <span className="op-kpi-v">{stats.lines}</span>
          <span className="op-kpi-sub">{rangeLabel}</span>
        </div>
        <div className="op-kpi">
          <span className="op-kpi-k">Units to make</span>
          <span className="op-kpi-v">{stats.units}</span>
          <span className="op-kpi-sub">across all sizes</span>
        </div>
        <div className="op-kpi">
          <span className="op-kpi-k">Funerals</span>
          <span className="op-kpi-v">{stats.funerals}</span>
          <span className="op-kpi-sub">{retailProducts.length} retail lines</span>
        </div>
        <div className="op-kpi">
          <span className="op-kpi-k">Unassigned</span>
          <span className={`op-kpi-v${stats.unassigned ? " is-warn" : " is-ok"}`}>
            {stats.unassigned}
          </span>
          <span className="op-kpi-sub">{stats.assigned} allocated</span>
        </div>
        <div className="op-kpi">
          <span className="op-kpi-k">Finished</span>
          <span className="op-kpi-v is-ok">{stats.done}</span>
          <span className="op-kpi-sub">end time recorded</span>
        </div>
      </div>

      {/* ---------------- body ---------------- */}
      <div className="op-body">
        <div className="op-main">
          {loading && (
            <div className="op-section">
              <div className="rf-empty">Loading ordered products…</div>
            </div>
          )}

          {!loading && loadError && (
            <div className="op-section">
              <div className="rf-empty">
                Couldn&apos;t load ordered products.{" "}
                <button
                  type="button"
                  className="rf-btn rf-btn-ghost"
                  onClick={refreshList}
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {!loading && !loadError && showFuneral && (
            <section className="op-section is-funeral">
              <div className="op-section-head">
                <span className="op-section-title">Funeral</span>
                <span className="op-section-meta rf-num">
                  {visibleFuneral.length} funerals ·{" "}
                  {visibleFuneral.reduce((n, g) => n + g.products.length, 0)} products
                </span>
              </div>
              <div className="op-scroll">
                <table className="op-table">
                  {head}
                  <tbody>
                    {visibleFuneral.length === 0 && (
                      <tr>
                        <td colSpan={colCount}>
                          <div className="rf-empty">
                            No funeral products for this range.
                          </div>
                        </td>
                      </tr>
                    )}
                    {visibleFuneral.map((group, gi) => {
                      const ids = group.products.map((p) => p._id);
                      return [
                        <tr className="op-grouprow" key={group.key}>
                          <td colSpan={colCount}>
                            <span className="op-group">
                              <span className="op-group-num">{gi + 1}</span>
                              <span className="op-group-name">{group.name}</span>
                              <span className="op-group-meta">
                                {group.products.length} products
                              </span>
                              <span className="rf-spacer" />
                              {assignMode && (
                                <span
                                  className="op-pickall"
                                  onClick={() => toggleMany(ids)}
                                >
                                  <span className={`op-box${groupState(ids)}`}>
                                    {groupState(ids) === " is-on"
                                      ? "✓"
                                      : groupState(ids) === " is-some"
                                      ? "–"
                                      : ""}
                                  </span>
                                  All in this funeral
                                </span>
                              )}
                            </span>
                          </td>
                        </tr>,
                        ...group.products.map((p, i) => productRow(p, i + 1)),
                      ];
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {!loading && !loadError && showRetail && (
            <section className="op-section is-retail">
              <div className="op-section-head">
                <span className="op-section-title">Retail</span>
                <span className="op-section-meta rf-num">
                  {visibleRetail.length} products
                </span>
                <span className="rf-spacer" />
                {assignMode && visibleRetail.length > 0 && (
                  <span
                    className="op-pickall"
                    onClick={() => toggleMany(visibleRetail.map((p) => p._id))}
                  >
                    <span
                      className={`op-box${groupState(
                        visibleRetail.map((p) => p._id)
                      )}`}
                    >
                      {groupState(visibleRetail.map((p) => p._id)) === " is-on"
                        ? "✓"
                        : groupState(visibleRetail.map((p) => p._id)) === " is-some"
                        ? "–"
                        : ""}
                    </span>
                    All retail
                  </span>
                )}
              </div>
              <div className="op-scroll">
                <table className="op-table">
                  {head}
                  <tbody>
                    {visibleRetail.length === 0 && (
                      <tr>
                        <td colSpan={colCount}>
                          <div className="rf-empty">
                            No retail products for this range.
                          </div>
                        </td>
                      </tr>
                    )}
                    {visibleRetail.map((p, i) => productRow(p, i + 1))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </div>

        {/* ---------------- aside ---------------- */}
        <aside className="op-side">
          {assignMode && (
            <div className="op-card">
              <div className="op-card-head">
                <span className="op-card-title">Allocate task</span>
                <span className="op-card-meta rf-num">{picked.length} picked</span>
              </div>
              <div className="op-alloc-note">
                {picked.length === 0 ? (
                  "Tick products in the list, then pick who makes them."
                ) : (
                  <>
                    <b>{picked.length}</b> products selected
                    {selectedStaff ? (
                      <>
                        {" "}
                        for <b>{selectedStaff.full_name}</b>
                      </>
                    ) : (
                      " — choose a staff member"
                    )}
                    .
                  </>
                )}
              </div>
              <div className="op-subhead">Staff</div>
              <div className="op-staff">
                {staffLoading && <div className="rf-empty">Loading staff…</div>}
                {!staffLoading && staff.length === 0 && (
                  <div className="rf-empty">No staff found.</div>
                )}
                {staff.map((u) => {
                  const active = selectedStaff && selectedStaff._id === u._id;
                  return (
                    <button
                      key={u._id}
                      type="button"
                      className={`op-staff-row${active ? " is-active" : ""}`}
                      onClick={() => setSelectedStaff(active ? null : u)}
                    >
                      <span className="op-avatar">{initials(u.full_name)}</span>
                      <span className="op-staff-name">{u.full_name || u.userName}</span>
                      <span className="op-staff-role">{u.access || u.role || ""}</span>
                    </button>
                  );
                })}
              </div>
              <div className="op-alloc-foot">
                <button
                  type="button"
                  className="rf-btn rf-btn-primary"
                  disabled={saving || !picked.length || !selectedStaff}
                  onClick={() => assign(false)}
                >
                  {saving ? "Saving…" : "Allocate"}
                </button>
                <button
                  type="button"
                  className="rf-btn op-btn-danger"
                  disabled={saving || !picked.length}
                  onClick={() => assign(true)}
                >
                  Deallocate
                </button>
              </div>
            </div>
          )}

          <div className="op-card">
            <div className="op-card-head">
              <span className="op-card-title">Count summary</span>
            </div>
            <div className="op-subhead">Product type</div>
            <Tally data={orderTally} />
            <div className="op-subhead">Order type</div>
            <Tally data={typeTally} />
          </div>
        </aside>
      </div>

      {/* ---------------- flower drawer ---------------- */}
      {showFlowers && (
        <div className="op-drawer-wrap">
          <div className="op-scrim" onClick={() => setShowFlowers(false)} />
          <div className="op-drawer">
            <div className="op-drawer-head">
              <div>
                <h3 className="op-drawer-title">Flower contents</h3>
                <div className="op-drawer-sub rf-num">{rangeLabel}</div>
              </div>
              <button
                type="button"
                className="rf-btn rf-btn-ghost op-export"
                onClick={exportFlowers}
                disabled={!Object.keys(flowerCount).length}
              >
                <MdTableView style={{ verticalAlign: "-2px" }} /> Export Excel
              </button>
              <button
                type="button"
                className="op-close"
                onClick={() => setShowFlowers(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="op-warn">
              Counts are indicative — products registered without a flower content
              are not represented here.
            </div>
            <div className="op-drawer-body">
              <Tally data={flowerCount} big alpha />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* A–Z by display name — how the shop reads the flower list off the page. */
const byName = (a, b) =>
  toName(a[0]).localeCompare(toName(b[0]), undefined, { sensitivity: "base" });

/* Key/value tally with a share bar — the redesigned SummaryTable. Biggest
   first by default; `alpha` sorts by name instead. */
function Tally({ data, big, alpha }) {
  const entries = Object.keys(data || {})
    .map((k) => [k, Number(data[k]) || 0])
    .sort(alpha ? byName : (a, b) => b[1] - a[1]);
  if (!entries.length) return <div className="rf-empty">Nothing counted yet.</div>;
  const max = Math.max(...entries.map((e) => e[1]), 1);
  return (
    <div className="op-tally">
      {entries.map(([k, v]) => (
        <div className="op-tally-row" key={k}>
          <span className="op-tally-k" title={k}>
            {toName(k)}
          </span>
          {big && (
            <span className="op-tally-bar">
              <span style={{ width: `${(v / max) * 100}%` }} />
            </span>
          )}
          <span className="op-tally-v rf-num">{v}</span>
        </div>
      ))}
    </div>
  );
}

export default OrderedProductsRedesign;
